import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PasswordInput,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { api, Dhis2Target, Permission } from '../lib/api';
import { useClientPagination } from '../lib/pagination';
import { Card, TablePagination, useToast } from '../components/ui';

/**
 * Where data goes. This screen is the answer to "configure the URLs where data should be sent to
 * DHIS2" - the base URL, credentials and import behaviour of every DHIS2 instance this hub feeds.
 */
export function Targets({ can }: { can: (permission: Permission) => boolean }) {
  const [rows, setRows] = useState<Dhis2Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Dhis2Target | 'new' | null>(null);
  const [testing, setTesting] = useState<number | null>(null);
  const pagination = useClientPagination(rows);
  const toast = useToast();

  const isAdmin = can('targets.write');
  const canTest = can('targets.test');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.get<Dhis2Target[]>('/api/targets'));
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function testTarget(target: Dhis2Target) {
    setTesting(target.id);
    try {
      const result = await api.post<{ ok: boolean; message: string }>(`/api/targets/${target.id}/test`);
      toast(result.ok ? 'ok' : 'error', `${target.name}: ${result.message}`);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setTesting(null);
    }
  }

  async function removeTarget(target: Dhis2Target) {
    if (!confirm(`Delete ${target.name}? Facilities pointing at it fall back to the default server.`)) {
      return;
    }
    try {
      await api.del(`/api/targets/${target.id}`);
      toast('ok', `${target.name} removed`);
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <div className="targets-toolbar">
        <span className="targets-toolbar-helper">
          Credentials are encrypted before they are stored and are never returned by the API.
        </span>
        {isAdmin ? (
          <Button
            kind="primary"
            size="md"
            renderIcon={Add}
            onClick={() => setEditing('new')}
          >
            Add DHIS2 server
          </Button>
        ) : null}
      </div>

      <Card bodyless>
        <div className="targets-results-panel">
          {loading && rows.length === 0 ? (
            <div className="targets-results-empty">
              <InlineLoading description="Loading servers…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="targets-results-empty">
              <div className="targets-empty-title">No DHIS2 server configured</div>
              <div className="targets-empty-desc">
                Add the DHIS2 instance this hub should deliver to, for example{' '}
                <span className="mono">https://dev.southsudanhis.org</span>.
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Server</TableHeader>
                    <TableHeader>Base URL</TableHeader>
                    <TableHeader>Authentication</TableHeader>
                    <TableHeader>Import strategy</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagination.pageItems.map((target) => (
                    <Fragment key={target.id}>
                      <TableRow>
                        <TableCell>
                          <div className="stack" style={{ gap: 4 }}>
                            <span style={{ fontWeight: 560 }}>{target.name}</span>
                            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                              {target.isDefault ? (
                                <Tag type="blue" size="sm" className="target-status-tag">
                                  Default
                                </Tag>
                              ) : null}
                              {!target.enabled ? (
                                <Tag type="gray" size="sm" className="target-status-tag">
                                  Disabled
                                </Tag>
                              ) : (
                                <Tag type="green" size="sm" className="target-status-tag">
                                  Enabled
                                </Tag>
                              )}
                              {target.dryRun ? (
                                <Tag type="purple" size="sm" className="target-status-tag">
                                  Dry run
                                </Tag>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="stack" style={{ gap: 2 }}>
                            <span className="mono">{target.baseUrl}</span>
                            <span className="faint small">
                              Posts to <span className="mono">{target.baseUrl}/api/dataValueSets</span>
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="stack" style={{ gap: 2 }}>
                            <span>
                              {target.authType === 'token'
                                ? 'Personal access token'
                                : target.authType === 'basic'
                                  ? `Basic (${target.username ?? 'no user'})`
                                  : 'None'}
                            </span>
                            {!target.hasSecret ? (
                              <span className="faint small">— no secret stored</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="mono">{target.importStrategy}</span>
                        </TableCell>
                        <TableCell>
                          {target.enabled ? (
                            <span className="muted small">Active</span>
                          ) : (
                            <span className="faint small">Disabled</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                            {canTest ? (
                              <Button
                                kind="ghost"
                                size="sm"
                                onClick={() => testTarget(target)}
                                disabled={testing === target.id}
                              >
                                {testing === target.id ? (
                                  <InlineLoading status="active" description="" />
                                ) : (
                                  'Test'
                                )}
                              </Button>
                            ) : null}
                            {isAdmin ? (
                              <>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  onClick={() => setEditing(target)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  kind="danger--ghost"
                                  size="sm"
                                  onClick={() => removeTarget(target)}
                                >
                                  Delete
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                      {target.dryRun ? (
                        <TableRow className="target-dryrun-row">
                          <TableCell colSpan={6} style={{ paddingTop: 0, paddingBottom: 12 }}>
                            <InlineNotification
                              kind="warning"
                              title="Dry run is on:"
                              subtitle="DHIS2 validates each payload and reports what it would import, but saves nothing."
                              hideCloseButton
                              lowContrast
                              style={{ maxWidth: '100%', margin: 0 }}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > 0 ? <TablePagination {...pagination} /> : null}
        </div>
      </Card>

      {editing ? (
        <TargetModal
          target={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}
    </>
  );
}

function TargetModal({
  target,
  onClose,
  onSaved,
}: {
  target: Dhis2Target | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(target?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(target?.baseUrl ?? '');
  const [authType, setAuthType] = useState(target?.authType ?? 'token');
  const [username, setUsername] = useState(target?.username ?? '');
  const [secret, setSecret] = useState('');
  const [importStrategy, setImportStrategy] = useState(target?.importStrategy ?? 'CREATE_AND_UPDATE');
  const [dryRun, setDryRun] = useState(target?.dryRun ?? true);
  const [enabled, setEnabled] = useState(target?.enabled ?? true);
  const [isDefault, setIsDefault] = useState(target?.isDefault ?? false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const urlValid = /^https?:\/\/.+/.test(baseUrl.trim());

  async function save() {
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        baseUrl: baseUrl.trim(),
        authType,
        username: authType === 'basic' ? username.trim() : null,
        // Blank means "keep the stored secret", so a rename does not require re-entering it.
        secret: secret.trim() || undefined,
        importStrategy,
        dryRun,
        enabled,
        isDefault,
      };
      if (target) {
        await api.patch(`/api/targets/${target.id}`, payload);
      } else {
        await api.post('/api/targets', payload);
      }
      toast('ok', `${payload.name} saved`);
      onSaved();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComposedModal open onClose={onClose} size="sm" className="targets-modal">
      <ModalHeader title={target ? `Edit ${target.name}` : 'Add DHIS2 server'} closeModal={onClose} />
      <ModalBody>
        <div className="targets-modal-form">
          <TextInput
            id="target-name-input"
            labelText="Name"
            placeholder="National DHIS2 (production)"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <TextInput
            id="target-baseurl-input"
            labelText="Base URL"
            helperText={
              urlValid || baseUrl === ''
                ? 'The DHIS2 root, without /api. The hub appends /api/dataValueSets.'
                : undefined
            }
            invalid={!urlValid && baseUrl !== ''}
            invalidText="Must start with http:// or https://"
            placeholder="https://dev.southsudanhis.org"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            className="targets-input-mono"
          />

          <Select
            id="target-authtype-select"
            labelText="Authentication"
            value={authType}
            onChange={(event) => setAuthType(event.target.value as typeof authType)}
          >
            <SelectItem value="token" text="Personal access token (recommended)" />
            <SelectItem value="basic" text="Username and password" />
            <SelectItem value="none" text="None" />
          </Select>

          {authType === 'basic' ? (
            <TextInput
              id="target-username-input"
              labelText="Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="off"
            />
          ) : null}

          {authType !== 'none' ? (
            <PasswordInput
              id="target-secret-input"
              labelText={authType === 'token' ? 'Access token' : 'Password'}
              helperText={target?.hasSecret ? 'A secret is already stored. Leave blank to keep it.' : undefined}
              placeholder={target?.hasSecret ? '••••••••' : ''}
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              autoComplete={authType === 'token' ? 'off' : 'new-password'}
            />
          ) : null}

          <Select
            id="target-importstrategy-select"
            labelText="Import strategy"
            helperText="CREATE_AND_UPDATE lets a corrected month overwrite what is already in DHIS2."
            value={importStrategy}
            onChange={(event) => setImportStrategy(event.target.value)}
          >
            <SelectItem value="CREATE_AND_UPDATE" text="CREATE_AND_UPDATE" />
            <SelectItem value="CREATE" text="CREATE" />
            <SelectItem value="UPDATE" text="UPDATE" />
          </Select>

          <Checkbox
            id="target-dryrun-checkbox"
            labelText="Dry run — validate against DHIS2 without saving"
            checked={dryRun}
            onChange={(_event, { checked }) => setDryRun(checked)}
          />

          <Checkbox
            id="target-enabled-checkbox"
            labelText="Enabled"
            checked={enabled}
            onChange={(_event, { checked }) => setEnabled(checked)}
          />

          <Checkbox
            id="target-default-checkbox"
            labelText="Default server for facilities with none assigned"
            checked={isDefault}
            onChange={(_event, { checked }) => setIsDefault(checked)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={save}
          disabled={busy || !name.trim() || !urlValid}
        >
          {busy ? <InlineLoading status="active" description="" /> : null}
          Save
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
